import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar, Box, Tooltip, Typography, useTheme } from '@mui/material';
const AvatarGroup = ({ users, maxDisplay = 3, size = 32, onClick, showTooltip = false, showNameForSingle = false, sx }) => {
    const theme = useTheme();
    const getUserInitials = (user) => {
        if (user.firstName || user.lastName) {
            const firstName = user.firstName || '';
            const lastName = user.lastName || '';
            return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
        }
        if (user.name) {
            const parts = user.name.split(' ');
            if (parts.length >= 2) {
                return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
            }
            return user.name[0]?.toUpperCase() || '?';
        }
        if (user.fullName) {
            const parts = user.fullName.split(' ');
            if (parts.length >= 2) {
                return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
            }
            return user.fullName[0]?.toUpperCase() || '?';
        }
        return '?';
    };
    const getAvatarSrc = (user) => {
        if (user.avatar && user.avatar !== '/placeholder.svg?height=24&width=24') {
            return user.avatar;
        }
        return undefined;
    };
    const getDisplayName = (user) => {
        if (user.fullName)
            return user.fullName;
        if (user.firstName || user.lastName) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim();
        }
        return user.name || '';
    };
    if (users.length === 0) {
        return null;
    }
    // Handle single avatar with optional name display
    if (users.length === 1 && showNameForSingle) {
        const user = users[0];
        const avatar = (_jsx(Avatar, { src: getAvatarSrc(user), alt: getDisplayName(user), sx: {
                width: size,
                height: size,
                border: `2px solid ${theme.palette.background.paper}`,
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.neutral,
                color: '#637381'
            }, children: !getAvatarSrc(user) ? getUserInitials(user) : null }));
        return (_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: onClick ? 'pointer' : 'default',
                ...sx
            }, onClick: onClick, children: [showTooltip ? (_jsx(Tooltip, { title: getDisplayName(user), arrow: true, children: avatar })) : (avatar), _jsx(Typography, { variant: 'body1', color: theme.palette.text.primary, children: getDisplayName(user) })] }));
    }
    // Handle multiple avatars
    const displayUsers = users.slice(0, maxDisplay);
    const remainingCount = users.length - maxDisplay;
    return (_jsxs(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            minWidth: remainingCount > 0
                ? size * maxDisplay + 8 + String(remainingCount).length * 10
                : size * displayUsers.length,
            pr: remainingCount > 0 ? 1 : 0,
            overflow: 'visible',
            cursor: onClick ? 'pointer' : 'default',
            ...sx
        }, onClick: onClick, children: [displayUsers.map((user, idx) => {
                const avatar = (_jsx(Avatar, { src: getAvatarSrc(user), alt: getDisplayName(user), sx: {
                        width: size,
                        height: size,
                        ml: idx === 0 ? 0 : '-8px',
                        border: `2px solid ${theme.palette.background.paper}`,
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: 1.5,
                        zIndex: maxDisplay - idx,
                        backgroundColor: theme.palette.background.neutral,
                        color: '#637381'
                    }, children: !getAvatarSrc(user) ? getUserInitials(user) : null }, user.id || idx));
                if (showTooltip) {
                    return (_jsx(Tooltip, { title: getDisplayName(user), arrow: true, children: avatar }, user.id || idx));
                }
                return avatar;
            }), remainingCount > 0 && (_jsxs(Avatar, { sx: {
                    width: size,
                    height: size,
                    ml: '-8px',
                    fontSize: '12px',
                    borderRadius: 1.5,
                    fontWeight: 500,
                    border: `2px solid ${theme.palette.background.paper}`,
                    backgroundColor: theme.palette.primary.lighter,
                    color: theme.palette.primary.dark,
                    zIndex: 0
                }, children: ["+", remainingCount] }))] }));
};
export default AvatarGroup;
